import { useMemo, useState } from "react";
import {
  Calculator,
  CheckCircle2,
  Sparkles,
  BarChart3,
  ArrowRight,
  Receipt,
  Percent,
} from "lucide-react";

function num(v: string) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function money(n: number) {
  if (!isFinite(n)) return "$0.00";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

type Mode = "sheet" | "job";

const DEFAULT_INK_ML_PER_SHEET = 7;
const DEFAULT_POWDER_G_PER_SHEET = 12;

function SectionTitle({
  icon: Icon,
  eyebrow,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <div className="mb-6">
      <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">
        <Icon className="h-4 w-4" />
        {eyebrow}
      </div>

      <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl">
        {title}
      </h2>

      <p className="mt-3 max-w-3xl text-sm leading-6 text-white/70 md:text-base">
        {description}
      </p>
    </div>
  );
}

export function CostCalculator() {
  const [mode, setMode] = useState<Mode>("sheet");

  const [sheetW, setSheetW] = useState("22");
  const [sheetH, setSheetH] = useState("60");

  const [filmPerSqft, setFilmPerSqft] = useState("0.55");
  const [inkPerLiter, setInkPerLiter] = useState("80");
  const [powderPerKg, setPowderPerKg] = useState("25");

  const [laborPerSheet, setLaborPerSheet] = useState("3.00");
  const [overheadPct, setOverheadPct] = useState("10");
  const [profitPct, setProfitPct] = useState("35");

  const [designW, setDesignW] = useState("10");
  const [designH, setDesignH] = useState("12");
  const [qty, setQty] = useState("24");

  const r = useMemo(() => {
    const w = num(sheetW);
    const h = num(sheetH);

    const sqft = (w * h) / 144;
    const film = sqft * num(filmPerSqft);

    const inkCostPerMl = num(inkPerLiter) / 1000;
    const ink = inkCostPerMl * DEFAULT_INK_ML_PER_SHEET;

    const powderCostPerG = num(powderPerKg) / 1000;
    const powder = powderCostPerG * DEFAULT_POWDER_G_PER_SHEET;

    const materials = film + ink + powder;
    const labor = num(laborPerSheet);

    const base = materials + labor;
    const overhead = base * (num(overheadPct) / 100);
    const cost = base + overhead;

    const profit = cost * (num(profitPct) / 100);
    const price = cost + profit;

    const sqftFormula = `(${w} × ${h}) ÷ 144 = ${sqft.toFixed(2)} sq ft`;

    const dW = num(designW);
    const dH = num(designH);
    const q = Math.max(0, Math.floor(num(qty)));

    const perRow = dW > 0 ? Math.floor(w / dW) : 0;
    const perCol = dH > 0 ? Math.floor(h / dH) : 0;
    const printsPerSheet = perRow * perCol;

    const sheetsNeeded =
      printsPerSheet > 0 && q > 0 ? Math.ceil(q / printsPerSheet) : 0;

    const jobCost = sheetsNeeded * cost;
    const jobPrice = sheetsNeeded * price;

    const costPerPrint = q > 0 ? jobCost / q : 0;
    const pricePerPrint = q > 0 ? jobPrice / q : 0;

    return {
      sqft,
      sqftFormula,
      film,
      ink,
      powder,
      materials,
      labor,
      base,
      overhead,
      cost,
      profit,
      price,
      perRow,
      perCol,
      printsPerSheet,
      sheetsNeeded,
      jobCost,
      jobPrice,
      costPerPrint,
      pricePerPrint,
    };
  }, [
    sheetW,
    sheetH,
    filmPerSqft,
    inkPerLiter,
    powderPerKg,
    laborPerSheet,
    overheadPct,
    profitPct,
    designW,
    designH,
    qty,
  ]);

  return (
    <div className="min-h-screen bg-[#070b14] text-white">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        {/* HERO */}
        <section className="relative overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-br from-[#091224] via-[#0b1530] to-[#080d18] p-6 md:p-10">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.16),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(217,70,239,0.12),transparent_30%)]" />

          <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-fuchsia-400/20 bg-fuchsia-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-fuchsia-200">
                <Calculator className="h-4 w-4" />
                Pricing Tool
              </div>

              <h1 className="mt-5 text-4xl font-black tracking-tight md:text-5xl">
                Cost Calculator
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-white/72 md:text-base">
                Estimate your DTF sheet cost, suggested pricing, and simple job
                profitability using film, ink, powder, labor, overhead, and
                markup inputs.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => setMode("sheet")}
                  className={`inline-flex items-center justify-center rounded-2xl border px-5 py-3 text-sm font-semibold transition ${
                    mode === "sheet"
                      ? "border-cyan-300/35 bg-cyan-400/20 text-cyan-100"
                      : "border-white/10 bg-white/5 text-white/80 hover:border-cyan-300/20 hover:bg-white/10"
                  }`}
                >
                  Sheet Mode
                </button>

                <button
                  onClick={() => setMode("job")}
                  className={`inline-flex items-center justify-center rounded-2xl border px-5 py-3 text-sm font-semibold transition ${
                    mode === "job"
                      ? "border-cyan-300/35 bg-cyan-400/20 text-cyan-100"
                      : "border-white/10 bg-white/5 text-white/80 hover:border-cyan-300/20 hover:bg-white/10"
                  }`}
                >
                  Job Mode
                </button>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {[
                {
                  title: "Need a fast quote?",
                  description:
                    "Use Sheet Mode for quick cost and suggested price per sheet.",
                },
                {
                  title: "Need a job estimate?",
                  description:
                    "Use Job Mode to estimate sheets needed, cost per print, and total sell price.",
                },
                {
                  title: "Need better pricing?",
                  description:
                    "Adjust labor, overhead, and markup to reflect your real shop workflow.",
                },
                {
                  title: "Need realistic material math?",
                  description:
                    "Film, ink, and powder are broken out separately so you can see where the money goes.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-[24px] border border-white/10 bg-white/[0.05] p-5"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-xl border border-emerald-300/20 bg-emerald-400/10 p-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                    </div>

                    <div>
                      <h3 className="text-sm font-bold text-white">{item.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-white/70">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="mt-8 grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          {/* INPUTS */}
          <section className="rounded-[26px] border border-white/10 bg-white/[0.04] p-5 md:p-6">
            <SectionTitle
              icon={Sparkles}
              eyebrow="Inputs"
              title="Dial in your real numbers"
              description="Enter your sheet size, material costs, labor, overhead, and profit targets to build a more realistic DTF pricing model."
            />

            <div className="grid gap-4 md:grid-cols-2">
              <Field
                label="Sheet Width (in)"
                value={sheetW}
                onChange={setSheetW}
                hint="Common DTF roll width is 22 inches."
              />

              <Field
                label="Sheet Height (in)"
                value={sheetH}
                onChange={setSheetH}
                hint="Length of the sheet you are quoting, such as 60 inches."
              />

              <Field
                label="Film Cost ($/sqft)"
                value={filmPerSqft}
                onChange={setFilmPerSqft}
                hint="Roll price divided by total square feet on the roll."
              />

              <Field
                label="Ink Cost ($/liter)"
                value={inkPerLiter}
                onChange={setInkPerLiter}
                hint="Use your actual ink purchase price per liter."
              />

              <Field
                label="Powder Cost ($/kg)"
                value={powderPerKg}
                onChange={setPowderPerKg}
                hint="Use your actual powder purchase price per kilogram."
              />

              <Field
                label="Labor per Sheet ($)"
                value={laborPerSheet}
                onChange={setLaborPerSheet}
                hint="Print, powder, cure, cut, pack, and related handling."
              />

              <Field
                label="Overhead (%)"
                value={overheadPct}
                onChange={setOverheadPct}
                hint="Shop costs, waste, utilities, and other operating drag."
              />

              <Field
                label="Markup / Profit (%)"
                value={profitPct}
                onChange={setProfitPct}
                hint="Your pricing target after true cost is covered."
              />
            </div>

            {mode === "job" ? (
              <div className="mt-6 rounded-[24px] border border-cyan-300/15 bg-cyan-400/[0.05] p-5">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
                  <Receipt className="h-4 w-4" />
                  Job Estimator Inputs
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <Field
                    label="Design Width (in)"
                    value={designW}
                    onChange={setDesignW}
                    hint="Single print width."
                  />

                  <Field
                    label="Design Height (in)"
                    value={designH}
                    onChange={setDesignH}
                    hint="Single print height."
                  />

                  <Field
                    label="Quantity"
                    value={qty}
                    onChange={setQty}
                    hint="How many prints you need."
                  />
                </div>
              </div>
            ) : null}
          </section>

          {/* RESULTS */}
          <section className="rounded-[26px] border border-white/10 bg-gradient-to-br from-[#091224] via-[#0b1530] to-[#080d18] p-5 md:p-6">
            <SectionTitle
              icon={BarChart3}
              eyebrow="Results"
              title="See your numbers clearly"
              description="Break down material cost, labor, overhead, and profit so your pricing is easier to understand and defend."
            />

            <div className="rounded-[22px] border border-white/10 bg-white/[0.05] p-5">
              <div className="text-sm text-white/65">Area</div>
              <div className="mt-1 text-3xl font-black text-white">
                {r.sqft.toFixed(2)} sqft
              </div>
              <div className="mt-2 text-xs text-white/50">{r.sqftFormula}</div>
            </div>

            <div className="mt-5 rounded-[22px] border border-white/10 bg-white/[0.05] p-5">
              <div className="space-y-3 text-sm">
                <Row label="Film" value={money(r.film)} />
                <Row
                  label="Ink"
                  value={money(r.ink)}
                  sub={`Estimated using standard sheet coverage @ ${money(
                    num(inkPerLiter)
                  )}/L`}
                />
                <Row
                  label="Powder"
                  value={money(r.powder)}
                  sub={`Estimated using standard powder usage @ ${money(
                    num(powderPerKg)
                  )}/kg`}
                />
                <div className="my-2 border-t border-white/10" />
                <Row label="Materials Total" value={money(r.materials)} strong />
                <Row label="Labor" value={money(r.labor)} />
                <div className="my-2 border-t border-white/10" />
                <Row label="Base Cost" value={money(r.base)} />
                <Row
                  label={`Overhead (${num(overheadPct)}%)`}
                  value={money(r.overhead)}
                />
                <Row label="Cost (pre-profit) / sheet" value={money(r.cost)} strong />
                <Row
                  label={`Profit (${num(profitPct)}%)`}
                  value={money(r.profit)}
                />
              </div>
            </div>

            {mode === "job" ? (
              <div className="mt-5 rounded-[22px] border border-cyan-300/15 bg-cyan-400/[0.05] p-5">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
                  <Receipt className="h-4 w-4" />
                  Job Estimator
                </div>

                <div className="space-y-3 text-sm">
                  <Row label="Fits per row" value={`${r.perRow}`} />
                  <Row label="Fits per column" value={`${r.perCol}`} />
                  <Row label="Prints per sheet" value={`${r.printsPerSheet}`} strong />
                  <Row label="Sheets needed" value={`${r.sheetsNeeded}`} strong />
                  <div className="my-2 border-t border-white/10" />
                  <Row label="Total job cost (pre-profit)" value={money(r.jobCost)} />
                  <Row label="Cost per print" value={money(r.costPerPrint)} strong />
                </div>
              </div>
            ) : null}

            <div className="mt-5 rounded-[24px] border border-cyan-300/25 bg-cyan-400/10 p-5 shadow-[0_0_20px_rgba(34,211,238,0.08)]">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
                <Percent className="h-4 w-4" />
                Suggested Price
              </div>

              <div className="mt-4 text-xs text-white/55">
                {mode === "job" ? "Per print target" : "Per sheet target"}
              </div>

              <div className="mt-1 text-4xl font-black text-white">
                {mode === "job" ? money(r.pricePerPrint) : money(r.price)}
              </div>

              {mode === "job" ? (
                <div className="mt-2 text-sm text-white/65">
                  Total suggested for job: {money(r.jobPrice)}
                </div>
              ) : null}
            </div>

            <div className="mt-5 rounded-[22px] border border-white/10 bg-black/15 p-4">
              <p className="text-xs leading-6 text-white/58">
                Estimate note: ink and powder are calculated using standard
                built-in usage assumptions for a typical sheet. Real costs can
                vary based on artwork coverage, white ink load, powder
                application, and waste.
              </p>
            </div>

            <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-cyan-300">
              Use your real shop costs for the best pricing model
              <ArrowRight className="h-4 w-4" />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <label className="block">
      <div className="mb-1 text-sm text-white/68">{props.label}</div>
      <input
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        inputMode="decimal"
        className="w-full rounded-2xl border border-white/10 bg-black/15 px-4 py-3 text-white outline-none transition focus:border-cyan-300/35 focus:bg-white/[0.04]"
      />
      {props.hint ? (
        <div className="mt-2 text-xs leading-5 text-white/45">{props.hint}</div>
      ) : null}
    </label>
  );
}

function Row(props: {
  label: string;
  value: string;
  strong?: boolean;
  sub?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className={`${props.strong ? "font-semibold text-white" : "text-white/68"}`}>
          {props.label}
        </div>
        {props.sub ? (
          <div className="text-xs leading-5 text-white/45">{props.sub}</div>
        ) : null}
      </div>
      <div className={`${props.strong ? "font-semibold text-white" : "text-white"}`}>
        {props.value}
      </div>
    </div>
  );
}